import { DatePipe } from '@angular/common';
import {
    Component,
    HostListener,
    OnDestroy,
    OnInit,
    computed,
    effect,
    inject,
    signal,
    ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { User } from '@models/user';
import { AuthService } from '@services/auth/auth.service';
import { CurrentlyEditedCardService } from '@services/currently-edited-card/currently-edited-card.service';
import { EnvConfigurationService } from '@services/env/env-configuration.service';
import { ErrorService } from '@services/error-handling/error-service';
import { TimeOptionsStateService } from '@services/state-management/time-options-state.service';
import { NoRoleModalComponent } from '@study/modals/no-role-modal/no-role-modal.component';
import { ModalSize, getModalConfig } from '@utility/modal-helper';
import { rxjsEffect } from '@utility/utility';
import {
    BehaviorSubject,
    Subject,
    Subscription,
    distinctUntilChanged,
    filter,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs';
import { IdleTimeService } from '../app/services/idle-time/idle-time.service';
import { AccessLevelTypes } from './enums/AccessLevelTypes';
import {
    DisplayErrorDialogComponent,
    DisplayErrorDialogData,
} from './v2/components/dialogs/display-error-dialog/display-error-dialog.component';
import { PatientEditRequestService } from './v2/services/patient-edit-request/patient-edit-request.service';
import { UserServiceV2 } from './v2/services/user-service/user.service';

@Component({
    selector: 'app-root',
    standalone: false,
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly patientEditRequestService = inject(PatientEditRequestService);
    private readonly authService = inject(AuthService);
    private readonly userServiceV2 = inject(UserServiceV2);
    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);
    private readonly currentlyEditedCardService = inject(CurrentlyEditedCardService);
    private readonly timeOptionsService = inject(TimeOptionsStateService);
    private readonly idleTimeService = inject(IdleTimeService);
    private readonly errorStateService = inject(ErrorService);
    private readonly titleService = inject(Title);
    private readonly envService = inject(EnvConfigurationService);
    loggedIn: boolean = false;
    user: User;
    isLoading: boolean = false;
    componentDestroyed$: Subject<boolean> = new Subject();
    isLayoutVisible: boolean = false;
    noRoleDialog: MatDialogRef<NoRoleModalComponent>;

    timeoutPromise: Promise<boolean>;
    timerSubscription: Subscription;

    httpError504$ = this.errorStateService.getHttpErrors().pipe(
        takeUntilDestroyed(),
        filter((error) => error.status === 504),
        switchMap(() => {
            return this.dialog
                .open<DisplayErrorDialogComponent, DisplayErrorDialogData>(DisplayErrorDialogComponent, {
                    ...getModalConfig(ModalSize.Small),
                    data: {
                        description:
                            'The server encountered a temporary issue and could not complete your request. Please try refreshing the page and report a bug if the problem persists.',
                    },
                })
                .afterClosed();
        })
    );

    // reload user permissions whenever page reloads
    clearPermissionsOnLogout$ = rxjsEffect(() =>
        this.authService.hasValidToken$.pipe(
            distinctUntilChanged(),
            tap((hasValidToken) => {
                if (!hasValidToken) {
                    // logout / not yet logged in - clear permissions
                    this.userServiceV2.currentUserData.clear();
                }
            })
        )
    );

    constructor() {
        this.httpError504$.subscribe();
        this.errorStateService
            .handleVersionError()
            .pipe(
                takeUntil(this.componentDestroyed$),
                switchMap((error) => {
                    let errorDialog = this.dialog.open<DisplayErrorDialogComponent, DisplayErrorDialogData>(
                        DisplayErrorDialogComponent,
                        {
                            ...getModalConfig(ModalSize.Small),
                            data: { description: error },
                        }
                    );
                    return errorDialog.afterClosed();
                }),
                tap(() => {
                    this.errorStateService.clearVersionError();
                })
            )
            .subscribe();

        this.errorStateService
            .handleUserMessageError()
            .pipe(
                takeUntil(this.componentDestroyed$),
                tap((error) => {
                    this.dialog.open<DisplayErrorDialogComponent, DisplayErrorDialogData>(DisplayErrorDialogComponent, {
                        ...getModalConfig(ModalSize.Small),
                        data: {
                            description: error.description,
                            title: error.title,
                            displayCloseButton: error.displayCloseButton,
                        },
                    });
                    this.errorStateService.clearMessageError();
                })
            )
            .subscribe();
    }

    readonly isAuthenticated$ = this.authService.getIsAuthenticated().pipe(distinctUntilChanged(), shareReplay(1));
    readonly isAuthSignal = toSignal(this.isAuthenticated$);

    readonly hasAccount$ = this.isAuthenticated$.pipe(
        filter(Boolean),
        switchMap(() => this.userServiceV2.hasAccount.get$()),
        shareReplay()
    );
    readonly hasAccountSignal = toSignal(this.hasAccount$);

    readonly userData$ = this.isAuthenticated$.pipe(
        filter(Boolean),
        switchMap(() => this.hasAccount$),
        filter(Boolean),
        switchMap(() => this.userServiceV2.currentUserData.get$()),
        takeUntilDestroyed()
    );
    readonly userDataSignal = toSignal(this.userData$);

    private readonly initPatientEditRequestsCount$ = effect(() => {
        const userData = this.userDataSignal();
        if (userData && userData.accessGroupId && userData.accessGroupId !== AccessLevelTypes.NoRole) {
            this.patientEditRequestService.submittedPatientEditRequestsCountInit.set(true);
        }
    });

    readonly routerLoad = computed(() => {
        const hasAccount = this.hasAccountSignal();
        const userData = this.userDataSignal();
        const isAccessRequestPage = this.isAccessRequestPage();

        if (isAccessRequestPage) return true; //create access request

        if (hasAccount === undefined) return false;

        if (userData && userData.accessGroupId && userData.accessGroupId !== AccessLevelTypes.NoRole) {
            //existing user with role
            this.initStaticData();
            return true;
        }

        if ((hasAccount && userData && userData.accessGroupId === AccessLevelTypes.NoRole) || !hasAccount) {
            //existing user without role
            if (this.noRoleDialog?.getState() === MatDialogState.OPEN) return false;
            this.openRoleDialog();
            return false;
        }

        return false;
    });

    readonly isAccessRequestPage = signal(false);

    ngOnInit() {
        this.titleService.setTitle(this.envService.configuration.applicationName);
        this.isAuthenticated$.subscribe();

        this.router.events
            .pipe(
                takeUntil(this.componentDestroyed$),
                filter((event) => event instanceof NavigationEnd),
                tap((event: NavigationEnd) => {
                    const url = event.url;
                    this.isAccessRequestPage.set(
                        (url.includes('actions') || url.includes('access-request')) &&
                            !url.includes('pending-access-requests')
                    );
                    this.currentlyEditedCardService.clearState();
                })
            )
            .subscribe(() => {
                this.currentlyEditedCardService.clearState();
            });

        this.timeoutPromise = this.idleTimeService.startTimeoutAsync(3600);
        this.setTimeoutAction();
    }

    setTimeoutAction() {
        if (!!this.timerSubscription) {
            this.timerSubscription.unsubscribe();
        }

        this.timerSubscription = this.idleTimeService.startTimer().subscribe();
        this.timeoutPromise.then((result: boolean) => {
            if (result) {
                this.authService.logout();
            }
        });
    }

    @HostListener('window:click')
    @HostListener('window:keypress')
    @HostListener('window:scroll')
    resetInactivityTimer() {
        this.timeoutPromise = this.idleTimeService.resetTimeoutAsync();
        this.setTimeoutAction();
    }

    lastActivityDateSubject: BehaviorSubject<number> = new BehaviorSubject<number>(Date.now());

    isAuth$ = this.lastActivityDateSubject.pipe(
        switchMap((_) => this.idleTimeService.sessionTimeExired$),
        tap((sessionExpired) => {
            if (sessionExpired === true) this.authService.logout();
        })
    );

    @HostListener('window:mousemove')
    lastActivityReset() {
        const now = Date.now();

        if (now - this.lastActivityDateSubject.value >= 1000) {
            this.lastActivityDateSubject.next(now);
        }
    }

    @HostListener('window:mousemove')
    checkTokenValidity() {
        const now = Date.now();

        if (now - this.lastActivityDateSubject.value >= 1000) {
            const expireAtFromSessionStorage = sessionStorage.getItem('expires_at');
            const tokenExpiration = this.authService.tokenExpiration();
            const isTokenExpired = tokenExpiration < now || tokenExpiration.toString() !== expireAtFromSessionStorage;

            if (tokenExpiration && isTokenExpired) this.authService.logout();
        }
    }

    ngOnDestroy() {
        this.componentDestroyed$.next(true);
        this.componentDestroyed$.complete();
    }

    private openRoleDialog(): void {
        this.noRoleDialog?.close();
        this.noRoleDialog = this.dialog.open<NoRoleModalComponent>(NoRoleModalComponent, {
            id: 'NoRoleDialog',
            ...getModalConfig(ModalSize.Small),
        });

        this.noRoleDialog.afterClosed().pipe(take(1)).subscribe();
    }

    private initStaticData(): void {
        const is24HourFormat: boolean = this.getIs24HourFormat();
        this.timeOptionsService.init(is24HourFormat);
    }

    private getIs24HourFormat(): boolean {
        try {
            return !new DatePipe(navigator.language) // use the builtin locale array to determine if am/pm appears
                .transform(new Date(0), 'shortTime')
                .toLowerCase()
                .match('am|pm');
        } catch (error) {
            console.warn(`Locale ${navigator.language} not found, getting format for en-US`);
            return !new DatePipe('en-US').transform(new Date(0), 'shortTime').toLowerCase().match('am|pm');
        }
    }
}
